import { useEffect, useState } from 'react';
import { Check, Copy, Share2 } from 'lucide-react';

import { useTranslation } from '@/utils/i18n';
import { formatInviteCode } from '@/utils/inviteCode';
import { createInviteLink } from '@/api/groups';
import { STORE_URL_IOS, STORE_URL_ANDROID } from '@/constants';
import { toast } from '@/stores/toast';
import BottomSheet from './BottomSheet';
import { cn } from '@/lib/utils';

/**
 * Invite-a-friend sheet (wireframe 3c): generates/reuses the circle's invite
 * link and lets the admin copy or share it.
 */
export default function InviteLinkSheet({
  open,
  groupId,
  groupName,
  onClose,
}: {
  open: boolean;
  groupId: number;
  groupName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setCode(null);
      setCopied(false);
      return;
    }
    setLoading(true);
    createInviteLink(groupId)
      .then(setCode)
      .catch(() => toast.error(t('groups.invite.shareError')))
      .finally(() => setLoading(false));
  }, [open, groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t('groups.invite.shareError'));
    }
  };

  // The button below copies a full, friendly invite message (invitation + store
  // links + the code) — never the bare code. Web Share is used only if the
  // platform already provides it; otherwise (and on cancel/failure) we copy.
  const share = async () => {
    if (!code) return;
    const message = t('groups.invite.shareMessage', {
      name: groupName,
      code,
      ios: STORE_URL_IOS,
      android: STORE_URL_ANDROID,
    });
    if (navigator.share) {
      try {
        await navigator.share({ text: message });
        return;
      } catch {
        /* cancelled or unsupported — fall through to clipboard copy */
      }
    }
    try {
      await navigator.clipboard.writeText(message);
      toast.success(t('groups.invite.messageCopied'));
    } catch {
      toast.error(t('groups.invite.shareError'));
    }
  };

  return (
    <BottomSheet
      open={open}
      title={t('groups.invite.shareButton')}
      onClose={onClose}
      testid="invite-sheet"
    >
      <div className="tw:flex tw:flex-col tw:gap-4 tw:px-1 tw:pb-2">
        {loading ? (
          <div className="tw:flex tw:justify-center tw:py-6">
            <span className="tw:size-6 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/20 tw:border-t-blue-primary" />
          </div>
        ) : code ? (
          <>
            <p className="tw:text-center tw:text-[13px] tw:text-grey-2">
              {t('groups.invite.codeHint')}
            </p>
            <button
              type="button"
              onClick={copy}
              data-testid="btn-invite-copy"
              className="tw:flex tw:items-center tw:justify-center tw:gap-3 tw:rounded-2xl tw:bg-dark-primary tw:px-4 tw:py-4"
            >
              <span
                className="tw:text-[26px] tw:font-bold tw:tracking-[0.3em] tw:text-white"
                data-testid="invite-code"
              >
                {formatInviteCode(code)}
              </span>
              <span className={cn(copied ? 'tw:text-green-3' : 'tw:text-grey-3')}>
                {copied ? <Check size={20} /> : <Copy size={20} />}
              </span>
            </button>

            <button
              type="button"
              onClick={share}
              data-testid="btn-invite-share"
              className="tw:flex tw:h-13 tw:w-full tw:items-center tw:justify-center tw:gap-2 tw:rounded-2xl tw:bg-blue-primary tw:py-3.5 tw:text-base tw:font-semibold tw:text-white tw:active:opacity-80"
            >
              <Share2 size={18} />
              {t('groups.invite.shareButton')}
            </button>
          </>
        ) : null}
      </div>
    </BottomSheet>
  );
}
